import { Suspense, useContext, useMemo, useState } from 'react';
import { AuditContext } from './useAuditController';
import { AuditSidebar } from '@dealstack/frontend/shared/components/Audit/AuditLayout/AuditSidebar.tsx';
import {
	StyledAuditContainer,
	StyledDetailPanel,
	StyledSessionDetails,
} from '@dealstack/frontend/shared/components/Audit/styled.tsx';
import { AuditDetailView } from '@dealstack/frontend/shared/components/Audit/AuditLayout/AuditDetailView.tsx';
import { SessionDetails } from '@dealstack/frontend/app/pages/Project/MIP/Messages/components/SessionDetails/SessionDetails';
import { EmptyAudits } from '@dealstack/frontend/shared/components/Audit/EmptyAudits/EmptyAudits.tsx';
import {
	DATE_FORMATS,
	formatStringDateToUiWithFallback,
} from '@dealstack/shared/lib/format/dates/date.ts';
import {
	formatAuditActionToDisplay,
	formatAuditSessionTitle,
} from '@dealstack/frontend/shared/components/Audit/AuditLayout/utils.ts';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';

export const AuditView = () => {
	const tCapManage = useTranslationCapManage();
	const { audits, isLoading, getTransformedAuditData } = useContext(AuditContext);
	const [selectedAuditId, setSelectedAuditId] = useState<string | null>(null);
	const selectedAudit = audits.find((audit) => audit.id === selectedAuditId) || null;

	const hasAudits = !!audits.length;

	const sessionActionTitle = useMemo(
		() => formatAuditSessionTitle(selectedAudit?.action, tCapManage),
		[selectedAudit, tCapManage]
	);

	if (!hasAudits) {
		return <EmptyAudits />;
	}

	return (
		<StyledAuditContainer>
			<AuditSidebar
				audits={audits}
				isLoading={isLoading}
				selectedAuditId={selectedAuditId}
				setSelectedAuditId={setSelectedAuditId}
			/>
			<StyledDetailPanel>
				{selectedAudit && (
					<AuditDetailView
						title={`${formatAuditActionToDisplay(selectedAudit.action, tCapManage)} by ${selectedAudit.userDisplayName}`}
						timestamp={formatStringDateToUiWithFallback(
							selectedAudit.createdAt,
							DATE_FORMATS.TIMELINE_DATE_TIME
						)}
						action={selectedAudit.action}
						data={getTransformedAuditData(selectedAudit)}
					/>
				)}
			</StyledDetailPanel>

			{selectedAudit?.userSession && (
				<StyledSessionDetails>
					<Suspense>
						<SessionDetails
							sessionTitle={sessionActionTitle}
							fullName={selectedAudit.userSession.fullName}
							email={selectedAudit.userSession.email}
							startDateAndTime={selectedAudit.userSession.createdAt}
							ipAddress={selectedAudit.userSession.ipAddress}
							location={selectedAudit.userSession.location}
							deviceInfo={selectedAudit.userSession.deviceInfo}
							isLoading={isLoading}
						/>
					</Suspense>
				</StyledSessionDetails>
			)}
		</StyledAuditContainer>
	);
};
